import { createContext, useContext } from 'react';
import type { IAsset, ProviderRegistry, ZapworksProvider } from '../providers';
import type { ILabel } from '@zappar/zw-api-client';

interface AssetActionsContextValue {
  zapworksProvider: ZapworksProvider | undefined;
  workspaceId: string;
  /** Provider registry containing external providers */
  registry?: ProviderRegistry;
  hasPersonalAssets?: boolean;
  onUpdateLabels?: (asset: IAsset, labels: ILabel[]) => Promise<IAsset | void>;
  onCreateLabel?: (name: string) => Promise<void>;
  onEditLabel?: (uid: string, newName: string) => Promise<void>;
  onDeleteLabel?: (uid: string) => Promise<void>;
  onViewLabelAnalytics?: (labelId: string) => void;
  onAddToProject?: (asset: IAsset, file?: File) => void;
  /**
   * Fires when the user begins dragging an asset card (after a small move
   * threshold). The host can use this to start a drag-to-place interaction,
   * e.g. dropping the asset onto a canvas. Receives the asset, the pointer
   * position at the moment the drag started, and the card's bounding rect (so
   * the host doesn't have to locate the card in the DOM itself).
   *
   * For external assets a lazy `requestFile` factory is also passed; calling it
   * starts the download, resolving with the file (or null if it failed).
   */
  onAssetDragStart?: (
    asset: IAsset,
    drag: { clientX: number; clientY: number; rect: DOMRect },
    requestFile?: () => Promise<File | null>
  ) => void;
  onAddToWorkspaceAssets?: (assets: IAsset[]) => Promise<void>;
  onMoveAllFilesToWorkspace?: (workspaceId: string) => Promise<void>;
  onDownloadCSV?: (error?: unknown) => void;
  /** Callback for opening the media library modal (tool mode only) */
  onOpenMediaLibraryModal?: () => void;
}

const AssetActionsContext = createContext<AssetActionsContextValue>({
  zapworksProvider: undefined,
  workspaceId: '',
  hasPersonalAssets: true,
});

export const useAssetActions = () => useContext(AssetActionsContext);

export const AssetActionsProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: AssetActionsContextValue;
}) => <AssetActionsContext.Provider value={value}>{children}</AssetActionsContext.Provider>;
